import React, { useMemo, useState, memo } from 'react';
import { Box, Paper, Typography, alpha } from '@mui/material';
import moment from 'moment';
import 'moment-timezone';
import BookingDetailsDrawer from './BookingDetailsDrawer';
import DateHeader from './DateHeader';
import UltraRoomGridCard from './UltraRoomGridCard';
import UltraRoomGridContainer from './UltraRoomGridContainer';
import { getBookingStatusPriority } from '../../utils/bookingUtils';
import { getHotelTodayDateString, isQuickBookDateAllowed } from '../../utils/dateUtils';
import { LOGIN_ACCENT } from '../auth/loginDarkTheme';
import { mobileAppGlassCard } from '../layout/mobile/mobileAppTheme';
import {
  getDashboardWorkspaceInnerSectionSx,
  getDashboardWorkspaceSectionHeaderSx,
  getDashboardWorkspaceSectionTitleSx,
} from '../../utils/dashboardWorkspaceLayout';
import { DASHBOARD_ACCENT } from '../../utils/dashboardSurfaceSx';
import { getUltraRoomStatusPalette } from '../../utils/dashboardUltraGridLayout';
import { isRoomDirty } from '../../utils/walkInHousekeeping';
import {
  getRoomMaintenanceBlockLabel,
  isRoomMaintenanceBlockedOn,
} from '../../utils/roomMaintenanceBlock';
import { isSameDayStay } from '../../utils/bookingStayDisplay';
import RoomBoardInventoryStats from '../../utils/roomBoardInventorySummary';
import { PERMISSIONS } from '../../auth/permissions';
import usePermission from '../../auth/usePermission';

/**
 * Booking Calendar Ultra View Component
 * Displays rooms in a compact grid view grouped by room type
 * Shows statistics: Total room, Vacant, Occupied, Booked, Blocked, Group
 */
const BookingCalendarUltraView = ({
  data,
  groupView = 'full',
  theme = 'light',
  onQuickBook,
  onWebCheckIn,
  onOpenWebCheckInInvite,
  onCheckout,
  onCheckIn,
  onEventBlock,
  onHousekeeping,
  onDirtyRoomHousekeeping,
  pendingCleaningByRoomId = {},
  layoutVariant = 'desktop',
  isLoading = false,
}) => {
  const { can } = usePermission();
  const canManageWebCheckIn = can(PERMISSIONS.WEB_CHECKIN_MANAGE);
  const { 
    rooms = [], 
    room_types = [], 
    bookings = [], 
    booking_items_by_date = {},
    blocked_rooms_by_date = {},
    start_date, 
    end_date 
  } = data || {};

  const isDark = theme === 'dark';
  const isMobileLayout = layoutVariant === 'mobile';
  const isWorkspaceLayout = layoutVariant === 'workspace';
  const sectionPaperSx = isMobileLayout
    ? {
        mb: 1.25,
        ...mobileAppGlassCard,
        borderRadius: '16px',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
      }
    : isWorkspaceLayout
      ? {
          ...getDashboardWorkspaceInnerSectionSx(theme),
          mb: 0,
          overflow: 'hidden',
        }
      : {
        mb: 3,
        background: isDark
          ? 'transparent'
          : 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'}`,
        borderRadius: '16px',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        boxShadow: isDark
          ? '0 4px 20px rgba(0, 0, 0, 0.3)'
          : '0 4px 20px rgba(0, 0, 0, 0.08)',
        '&:hover': {
          boxShadow: isDark
            ? '0 8px 32px rgba(0, 0, 0, 0.4)'
            : '0 8px 32px rgba(0, 0, 0, 0.12)',
          transform: 'translateY(-2px)',
        },
      };
  const sectionHeaderSx = isMobileLayout
    ? {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0.75,
        p: 1,
        background: 'rgba(255,255,255,0.03)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }
    : isWorkspaceLayout
      ? {
          ...getDashboardWorkspaceSectionHeaderSx(theme, DASHBOARD_ACCENT),
          position: 'relative',
          zIndex: 2,
        }
      : {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 1.5,
        background: isDark
          ? 'transparent'
          : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #f5f5f5 100%)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-1px)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: isDark
            ? 'linear-gradient(90deg, rgba(76, 225, 182, 0.6) 0%, rgba(76, 225, 182, 0.4) 25%, rgba(76, 225, 182, 0.3) 50%, rgba(76, 225, 182, 0.4) 75%, rgba(76, 225, 182, 0.6) 100%)'
            : 'linear-gradient(90deg, rgba(76, 225, 182, 0.7) 0%, rgba(76, 225, 182, 0.5) 25%, rgba(76, 225, 182, 0.4) 50%, rgba(76, 225, 182, 0.5) 75%, rgba(76, 225, 182, 0.7) 100%)',
          zIndex: 1,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: isDark
            ? 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.05) 50%, transparent 100%)'
            : 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
          animation: 'shimmer 3s infinite',
          zIndex: 0,
          '@keyframes shimmer': {
            '0%': { left: '-100%' },
            '100%': { left: '100%' },
          },
        },
      };
  const sectionTitleSx = isMobileLayout
    ? {
        fontWeight: 800,
        fontSize: '0.82rem',
        color: '#f8fafc',
        letterSpacing: '0.02em',
      }
    : isWorkspaceLayout
      ? getDashboardWorkspaceSectionTitleSx(theme)
      : {
        fontWeight: 700,
        fontSize: '0.95rem',
        background: isDark
          ? 'linear-gradient(135deg, #ddd 0%, #b0b0b0 100%)'
          : 'linear-gradient(135deg, #212121 0%, #424242 100%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        letterSpacing: '0.5px',
        textShadow: isDark
          ? '0 1px 2px rgba(0, 0, 0, 0.5)'
          : '0 1px 2px rgba(255, 255, 255, 0.8)',
      };
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const renderGroupStats = (groupCounts) => (
    <RoomBoardInventoryStats
      groupCounts={groupCounts}
      themeMode={theme}
      dense={isMobileLayout}
      compact={!isMobileLayout && isWorkspaceLayout}
      isLoading={isLoading}
      justifyContent={isMobileLayout ? 'flex-start' : (isWorkspaceLayout ? 'flex-end' : 'flex-start')}
    />
  );

  const renderSectionTitle = (title) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobileLayout ? 0.65 : 0.75, minWidth: 0 }}>
      {!isWorkspaceLayout || isMobileLayout ? (
        <Box
          sx={{
            width: isMobileLayout ? 8 : 10,
            height: isMobileLayout ? 8 : 10,
            borderRadius: '50%',
            background: isMobileLayout
              ? LOGIN_ACCENT
              : 'linear-gradient(135deg, #4ce1b6 0%, #3ab896 50%, #2a9d7a 100%)',
            boxShadow: isMobileLayout
              ? `0 0 10px ${alpha(LOGIN_ACCENT, 0.45)}`
              : 'inset 0 1px 2px rgba(255, 255, 255, 0.3), 0 2px 8px rgba(76, 225, 182, 0.5), 0 1px 4px rgba(76, 225, 182, 0.4)',
            flexShrink: 0,
          }}
        />
      ) : null}
      <Typography variant="h6" sx={sectionTitleSx}>
        {isWorkspaceLayout && !isMobileLayout ? title.toUpperCase() : title}
      </Typography>
    </Box>
  );

  // Get hotel timezone from Django context
  const djangoContext = window.DJANGO_CONTEXT || {};
  const selectedHotel = djangoContext.selectedHotel || {};
  const hotelTimezone = selectedHotel.timezone || (moment.tz ? moment.tz.guess() : 'UTC');
  
  // Helper function to create moment objects in hotel timezone
  const momentInHotelTz = (dateInput) => {
    if (!dateInput) {
      return moment.tz ? moment.tz(hotelTimezone) : moment();
    }
    if (moment.tz) {
      return moment.tz(dateInput, hotelTimezone);
    }
    return moment(dateInput);
  };

  // Get the selected date (use first date in range for ultra view) in hotel timezone
  const selectedDateStr = start_date || new Date().toISOString().split('T')[0];
  const hotelTodayStr = getHotelTodayDateString(hotelTimezone);
  const selectedDateMoment = momentInHotelTz(selectedDateStr);
  const getHotelDateKey = (value) => {
    if (!value) return '';
    const parsed = momentInHotelTz(value);
    if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
    return String(value).slice(0, 10);
  };
  
  // Create date info object for DateHeader component
  const currentDateStr = moment().format('YYYY-MM-DD');
  const isToday = selectedDateStr === currentDateStr;
  const dateInfo = {
    key: selectedDateStr,
    day: selectedDateMoment.format('ddd'),
    dayNum: selectedDateMoment.format('D'),
    month: selectedDateMoment.format('MMM'),
    year: selectedDateMoment.format('YYYY'),
  };
  const selectedDateItems = booking_items_by_date?.[selectedDateStr] || {};

  // Map bookings by room id using the new booking_items_by_date structure
  // Use the same logic as grid view: check date ranges from check_in_date and check_out_date
  const bookingsByRoomId = {};
  const bookingsByRoomTypeId = {};
  const blockedRoomsByRoomId = {};
  const blockedRoomsByRoomTypeId = {};
  const getFallbackStatusColor = (status) => {
    // Room cards: only true checked_in is green. Booking-level partial_* is not room status.
    if (status === 'checked_in') return 'green';
    if (status === 'checked_out') return 'gray';
    if (['cancelled', 'no_show'].includes(status)) return 'red';
    return 'orange';
  };
  const resolveRoomCardItemStatus = (bookingLike) => {
    if (!bookingLike) return '';
    const itemStatus = String(bookingLike.item_status || '').toLowerCase();
    if (itemStatus === 'checked_out' || bookingLike.checkout === true) {
      return 'checked_out';
    }
    if (itemStatus === 'checked_in' || bookingLike.checked_in === true) return 'checked_in';
    if (itemStatus) return itemStatus;
    const bookStatus = String(bookingLike.status || '').toLowerCase();
    // Partial rollup without per-room flags → booked. Full checked_in (legacy) stays green.
    if (bookStatus === 'partial_checkin' || bookStatus === 'partial_checkout') {
      return 'confirmed';
    }
    return bookStatus;
  };
  const getAllocationKey = (bookingLike) => {
    if (!bookingLike) return null;
    if (bookingLike._allocation_key) return bookingLike._allocation_key;
    // Prefer logical room stream over nightly booking_item_id so multi-night
    // unallocated stays do not allocate once per night.
    const bookingId = bookingLike.booking_id || bookingLike.id;
    const roomTypeId = bookingLike.room_type_id || 'none';
    const sequence = bookingLike.room_sequence || 1;
    if (bookingId != null) {
      return `booking_${bookingId}_${roomTypeId}_${sequence}`;
    }
    if (bookingLike.booking_item_id != null) return `item_${bookingLike.booking_item_id}`;
    const checkIn = bookingLike.check_in_date || bookingLike.start_date || bookingLike.start || '';
    const checkOut = bookingLike.check_out_date || bookingLike.end_date || bookingLike.end || '';
    return `booking_${bookingId}_${roomTypeId}_${sequence}_${checkIn}_${checkOut}`;
  };
  const TERMINAL_ROOM_STATUSES = new Set(['checked_out', 'cancelled', 'canceled', 'no_show']);
  const isBlockingRoomBooking = (bookingLike) => {
    if (!bookingLike) return false;
    const normalizedStatus = String(bookingLike.item_status || bookingLike.status || '').toLowerCase();
    return !TERMINAL_ROOM_STATUSES.has(normalizedStatus);
  };
  const isTerminalRoomBooking = (bookingLike) => {
    if (!bookingLike) return false;
    if (bookingLike.checkout === true) return true;
    const normalizedStatus = String(bookingLike.item_status || bookingLike.status || '').toLowerCase();
    return TERMINAL_ROOM_STATUSES.has(normalizedStatus);
  };
  const getBookingCardRoomIds = (bookingLike) => {
    if (!bookingLike) return [];
    const ids = new Set();
    const addId = (raw) => {
      const id = Number(raw);
      if (Number.isFinite(id)) ids.add(id);
    };
    if (Array.isArray(bookingLike.room_ids)) {
      bookingLike.room_ids.forEach(addId);
    }
    addId(bookingLike.room_id);
    if (Array.isArray(bookingLike.checked_out_room_ids)) {
      bookingLike.checked_out_room_ids.forEach(addId);
    }
    if (Array.isArray(bookingLike.room_segments)) {
      bookingLike.room_segments.forEach((segment) => addId(segment?.room_id));
    }
    return [...ids];
  };
  const getBookingIdentity = (bookingLike) => String(bookingLike?.booking_id || bookingLike?.id || '');
  const isRoomCheckedOutForBooking = (bookingLike, roomId) => {
    if (!bookingLike || roomId == null) return false;
    const roomIdNum = Number(roomId);
    if (!Number.isFinite(roomIdNum)) return false;
    const bookingId = getBookingIdentity(bookingLike);

    const parentBooking = bookingId
      ? (bookings || []).find((b) => getBookingIdentity(b) === bookingId)
      : null;
    const bookingSources = parentBooking && parentBooking !== bookingLike
      ? [bookingLike, parentBooking]
      : [bookingLike];

    for (const source of bookingSources) {
      const vacatedRoomIds = Array.isArray(source?.checked_out_room_ids)
        ? source.checked_out_room_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id))
        : [];
      if (vacatedRoomIds.includes(roomIdNum)) return true;

      const matchingSegment = (Array.isArray(source?.room_segments) ? source.room_segments : [])
        .find((seg) => Number(seg?.room_id) === roomIdNum);
      if (matchingSegment) {
        const segmentStatus = String(matchingSegment.item_status || '').toLowerCase();
        if (matchingSegment.checkout === true || segmentStatus === 'checked_out') return true;
      }
    }

    const selectedRoomItems = Object.values(selectedDateItems || {}).flatMap((byRoomType) => {
      const byRoom = byRoomType || {};
      return byRoom[String(roomIdNum)] || byRoom[roomIdNum] || [];
    });
    return selectedRoomItems.some((item) => {
      if (bookingId && getBookingIdentity(item) !== bookingId) return false;
      const itemStatus = String(item?.item_status || item?.status || '').toLowerCase();
      return item?.checkout === true || itemStatus === 'checked_out';
    });
  };
  /** Chronological "booking sequence" time — later events win for terminal room cards. */
  const getBookingSequenceTime = (bookingLike) => {
    if (!bookingLike) return 0;
    const candidates = [
      bookingLike.segment_ended_at,
      bookingLike.check_out_time,
      bookingLike.room_changed_at,
      bookingLike.segment_started_at,
      bookingLike.check_in_time,
      bookingLike.updated_at,
      bookingLike.created_at,
      bookingLike.check_out_date,
      bookingLike.check_in_date,
    ];
    for (let i = 0; i < candidates.length; i += 1) {
      const raw = candidates[i];
      if (!raw) continue;
      const ms = moment(raw).valueOf();
      if (Number.isFinite(ms)) return ms;
    }
    const codeSequence = Number(String(bookingLike.code || bookingLike.booking_code || '').match(/(\d+)$/)?.[1] || 0);
    if (Number.isFinite(codeSequence) && codeSequence > 0) return codeSequence;
    const id = Number(bookingLike.booking_id || bookingLike.id || 0);
    return Number.isFinite(id) ? id : 0;
  };
  const getBookingSequenceNumber = (bookingLike) => {
    if (!bookingLike) return 0;
    const codeSequence = Number(String(bookingLike.code || bookingLike.booking_code || '').match(/(\d+)$/)?.[1] || 0);
    if (Number.isFinite(codeSequence) && codeSequence > 0) return codeSequence;
    const id = Number(bookingLike.booking_id || bookingLike.id || 0);
    return Number.isFinite(id) ? id : 0;
  };
  /**
   * Decide whether candidate should replace existing on a room card.
   * Active stays always beat terminal. Same tier: higher status priority wins
   * (cancelled/no_show are lowest so OUT / last real stay is preferred).
   * Equal priority: latest booking sequence wins.
   */
  const shouldReplaceRoomBooking = (existingBooking, candidateBooking) => {
    if (!existingBooking) return true;
    if (!candidateBooking) return false;
    if (getBookingIdentity(existingBooking) === getBookingIdentity(candidateBooking)) return true;
    const existingActive = isBlockingRoomBooking(existingBooking);
    const candidateActive = isBlockingRoomBooking(candidateBooking);
    if (candidateActive && !existingActive) return true;
    if (!candidateActive && existingActive) return false;
    const priorityDiff =
      getBookingStatusPriority(candidateBooking?.status, candidateBooking?.item_status)
      - getBookingStatusPriority(existingBooking?.status, existingBooking?.item_status);
    if (priorityDiff !== 0) return priorityDiff > 0;
    const existingStatus = String(existingBooking?.item_status || existingBooking?.status || '').toLowerCase();
    const candidateStatus = String(candidateBooking?.item_status || candidateBooking?.status || '').toLowerCase();
    if (existingStatus === 'checked_out' && candidateStatus === 'checked_out') {
      const sequenceDiff = getBookingSequenceNumber(candidateBooking) - getBookingSequenceNumber(existingBooking);
      if (sequenceDiff !== 0) return sequenceDiff > 0;
    }
    const timeDiff = getBookingSequenceTime(candidateBooking) - getBookingSequenceTime(existingBooking);
    if (timeDiff !== 0) return timeDiff > 0;
    return (
      Number(candidateBooking?.booking_id || candidateBooking?.id || 0)
      >= Number(existingBooking?.booking_id || existingBooking?.id || 0)
    );
  };
  const canUseRoomForBooking = (existingBooking, candidateBooking) => {
    if (!existingBooking) return true;
    if (getBookingIdentity(existingBooking) === getBookingIdentity(candidateBooking)) return true;
    // Never displace an active in-house / booked stay.
    if (isBlockingRoomBooking(existingBooking)) return false;
    // Terminal card: overwrite only with higher-priority status or later same-status booking.
    return shouldReplaceRoomBooking(existingBooking, candidateBooking);
  };
  /**
   * Prefer active bookings by status priority; if only terminal remain,
   * prefer checkout/OUT over cancelled, then latest sequence.
   */
  const pickPreferredRoomBookingItem = (items = []) => {
    if (!items || items.length === 0) return null;
    const list = [...items];
    const active = list.filter((it) => !isTerminalRoomBooking(it));
    const pool = active.length > 0 ? active : list;
    return pool.sort((a, b) => {
      const priorityDiff =
        getBookingStatusPriority(b?.status, b?.item_status)
        - getBookingStatusPriority(a?.status, a?.item_status);
      if (priorityDiff !== 0) return priorityDiff;
      const timeDiff = getBookingSequenceTime(b) - getBookingSequenceTime(a);
      if (timeDiff !== 0) return timeDiff;
      return Number(b?.booking_id || b?.id || 0) - Number(a?.booking_id || a?.id || 0);
    })[0];
  };
  
  // Process booking_items_by_date for selected date only.
  // This keeps confirmed occupancy aligned with room-type availability for this specific day.
  const hasSelectedDateItemData = Object.keys(selectedDateItems).length > 0;
  Object.keys(selectedDateItems).forEach((roomTypeId) => {
    const roomTypeData = selectedDateItems[roomTypeId] || {};
    Object.keys(roomTypeData).forEach((roomId) => {
      const bookingItems = roomTypeData[roomId];
      const visibleBookingItems = [...(bookingItems || [])];
      if (visibleBookingItems.length === 0) return;

      const roomIdNum = Number.parseInt(roomId, 10);
      const hasAllocatedRoom = Number.isFinite(roomIdNum);

      if (hasAllocatedRoom) {
        // If multiple terminal bookings share a room/date, keep the latest checkout card.
        const prioritizedItem = pickPreferredRoomBookingItem(visibleBookingItems);
        if (!prioritizedItem) return;
        const checkInDate = prioritizedItem?.check_in_date || selectedDateStr;
        const checkOutDate = prioritizedItem?.check_out_date || selectedDateStr;
        const roomItemStatus = resolveRoomCardItemStatus(prioritizedItem);
        const candidate = {
          ...prioritizedItem,
          id: prioritizedItem.booking_id || prioritizedItem.id,
          booking_id: prioritizedItem.booking_id,
          room_type_id: Number.parseInt(roomTypeId, 10) || prioritizedItem.room_type_id || null,
          check_in_date: checkInDate,
          check_out_date: checkOutDate,
          item_status: roomItemStatus || prioritizedItem.item_status,
          status_color:
            prioritizedItem.status_color
            || getFallbackStatusColor(roomItemStatus || prioritizedItem?.item_status || prioritizedItem?.status),
          _allocation_key: getAllocationKey(prioritizedItem),
        };
        if (shouldReplaceRoomBooking(bookingsByRoomId[roomIdNum], candidate)) {
          bookingsByRoomId[roomIdNum] = candidate;
        }
        return;
      }

      // No allocated room: one room requirement per logical stream (booking + sequence),
      // not per nightly booking_item row.
      visibleBookingItems.forEach((item, idx) => {
        if (!item?.booking_id) return;
        const checkInDate = item.check_in_date || selectedDateStr;
        const checkOutDate = item.check_out_date || selectedDateStr;
        const isSameDay = checkInDate === checkOutDate;
        const isActiveOnSelectedDate = isSameDay
          ? checkInDate <= selectedDateStr && checkOutDate >= selectedDateStr
          : checkInDate <= selectedDateStr && checkOutDate > selectedDateStr;
        if (!isActiveOnSelectedDate) return;

        const rtId = Number.parseInt(roomTypeId, 10) || item.room_type_id || null;
        if (!rtId) return;
        if (!bookingsByRoomTypeId[rtId]) bookingsByRoomTypeId[rtId] = [];

        const roomSequence = item.room_sequence || idx + 1;
        const streamKey = `booking_${item.booking_id}_${rtId}_${roomSequence}`;
        if (bookingsByRoomTypeId[rtId].some((existing) => (
          existing._allocation_key === streamKey
          || getAllocationKey(existing) === streamKey
        ))) {
          return;
        }

        const normalized = {
          ...item,
          id: item.booking_id || item.id,
          booking_id: item.booking_id,
          room_type_id: rtId,
          room_sequence: roomSequence,
          check_in_date: checkInDate,
          check_out_date: checkOutDate,
          status_color: item.status_color || getFallbackStatusColor(item?.status),
          _allocation_key: streamKey,
        };
        bookingsByRoomTypeId[rtId].push(normalized);
      });
    });
  });
  
  // Process blocked_rooms_by_date for ALL dates in the range
  // Collect blocks that overlap with the selected date based on their original_start_date and original_end_date
  // A block from 20-24 is active on dates 20, 21, 22, 23 (not 24, as end_date is exclusive)
  const blocksForSelectedDate = {}; // {roomTypeId: blockInfo}
  
  if (blocked_rooms_by_date) {
    // Collect all unique blocks first
    const uniqueBlocks = new Map(); // Key: `${roomTypeId}_${original_start_date}_${original_end_date}`
    
    Object.keys(blocked_rooms_by_date).forEach(dateStr => {
      const dateData = blocked_rooms_by_date[dateStr];
      Object.keys(dateData).forEach(roomTypeId => {
        const blockInfo = dateData[roomTypeId];
        if (blockInfo.original_start_date && blockInfo.original_end_date) {
          const blockKey = `${roomTypeId}_${blockInfo.original_start_date}_${blockInfo.original_end_date}`;
          if (!uniqueBlocks.has(blockKey)) {
            uniqueBlocks.set(blockKey, {
              roomTypeId,
              blockInfo,
            });
          }
        }
      });
    });
    
    // Check which blocks are active on the selected date
    uniqueBlocks.forEach(({ roomTypeId, blockInfo }) => {
      const blockStart = momentInHotelTz(blockInfo.original_start_date).startOf('day');
      const blockEnd = momentInHotelTz(blockInfo.original_end_date).startOf('day');
      const selectedDate = selectedDateMoment.startOf('day');
      
      // original_end_date is exclusive (unblock date), same as booking checkout.
      // Block 15→16 is active only on 15.
      const selectedDateFormatted = selectedDate.format('YYYY-MM-DD');
      const blockStartStr = blockStart.format('YYYY-MM-DD');
      const blockEndStr = blockEnd.format('YYYY-MM-DD');
      
      const isActiveOnSelectedDate =
        selectedDateFormatted >= blockStartStr && selectedDateFormatted < blockEndStr;
      
      if (isActiveOnSelectedDate) {
        // Use the block info if we don't have one yet for this room type
        if (!blocksForSelectedDate[roomTypeId]) {
          blocksForSelectedDate[roomTypeId] = blockInfo;
        }
      }
    });
    
    // REMOVED FALLBACK: The fallback was causing blocks to show on end dates
    // because the backend creates entries for all dates including the end date.
    // We now rely solely on the date range logic above.
  }
  
  // Set blocked rooms for the selected date
  Object.keys(blocksForSelectedDate).forEach(roomTypeId => {
    blockedRoomsByRoomTypeId[roomTypeId] = blocksForSelectedDate[roomTypeId];
  });
  
  // Fallback to old bookings structure for backward compatibility.
  // If selected-date item data is present, it is the source of truth and fallback is skipped.
  if (!hasSelectedDateItemData) {
    bookings.forEach(b => {
    const bookingStart = b.start || b.start_date || b.check_in_date;
    // For checked-out bookings, check_out_date now contains the actual checkout date (from check_out_time)
    // For other bookings, it contains the scheduled checkout date
    const bookingEnd = b.end || b.end_date || b.check_out_date;
    
    // Check if booking overlaps with the selected date
    // Note: bookingEnd uses actual checkout date for checked-out bookings (from API)
    // For same-day bookings (checkInDate === checkOutDate): show on that date (inclusive)
    // For multi-day bookings (checkInDate < checkOutDate): show only up to but not including checkout date (exclusive)
    const isSameDay = bookingStart === bookingEnd;
    const isActiveOnDate = isSameDay
      ? bookingStart && bookingEnd && bookingStart <= selectedDateStr && bookingEnd >= selectedDateStr
      : bookingStart && bookingEnd && bookingStart <= selectedDateStr && bookingEnd > selectedDateStr;
    
    if (!isActiveOnDate) return;
    
    // If booking has allocated rooms, map by room_id using room-card priority.
    const fallbackRoomIds = getBookingCardRoomIds(b);
    if (fallbackRoomIds.length > 0) {
      fallbackRoomIds.forEach(roomId => {
        const roomIdNum = Number(roomId);
        if (!Number.isFinite(roomIdNum)) return;
        const candidate = {
          ...b,
          status_color: b.status_color || getFallbackStatusColor(b.status),
          _allocation_key: getAllocationKey(b),
        };
        if (shouldReplaceRoomBooking(bookingsByRoomId[roomIdNum], candidate)) {
          bookingsByRoomId[roomIdNum] = candidate;
        }
      });
    }
    
    // Also map by room_type_id for bookings without allocated rooms
    if (b.room_type_id && (!b.room_ids || b.room_ids.length === 0)) {
      // If selected-date item data already provided room-type entries, don't add coarse fallback rows.
      const existingForBooking = (bookingsByRoomTypeId[b.room_type_id] || []).some(
        (x) => (x.booking_id || x.id) === (b.id || b.booking_id)
      );
      if (existingForBooking) return;
      if (!bookingsByRoomTypeId[b.room_type_id]) {
        bookingsByRoomTypeId[b.room_type_id] = [];
      }
      bookingsByRoomTypeId[b.room_type_id].push({
        ...b,
        status_color: b.status_color || getFallbackStatusColor(b.status),
        _allocation_key: getAllocationKey(b),
      });
    }
    });
  }

  // Bind in-house guests whose checkout is due (or overdue) on the selected date.
  // Nightly booking items stop on the night before checkout, so checkout-morning rooms
  // would otherwise look vacant even though the guest is still occupying the room.
  const inHouseStatuses = new Set(['checked_in', 'partial_checkin', 'partial_checkout']);
  const roomHasOtherBookingOnSelectedDate = (roomIdNum, pendingBooking) => {
    const pendingId = getBookingIdentity(pendingBooking);
    const roomKey = String(roomIdNum);
    const terminal = new Set(['checked_out', 'cancelled', 'canceled', 'no_show']);

    // Explicit allocations on the selected date for this room (arrivals / overlapping stays).
    for (const roomTypeId of Object.keys(selectedDateItems)) {
      const roomTypeData = selectedDateItems[roomTypeId] || {};
      const items = roomTypeData[roomKey] || roomTypeData[roomIdNum] || [];
      for (const item of items) {
        if (!item) continue;
        if (getBookingIdentity(item) === pendingId) continue;
        const itemStatus = String(item.item_status || item.status || '').toLowerCase();
        if (terminal.has(itemStatus)) continue;
        // Any other non-terminal stay/reservation on this room blocks walk-in.
        if (item.booking_id || item.id || itemStatus) return true;
      }
    }

    // Coarse booking list: another booking already assigned to this room for the day.
    for (const b of bookings || []) {
      if (getBookingIdentity(b) === pendingId) continue;
      const status = String(b.status || '').toLowerCase();
      if (terminal.has(status)) continue;
      const roomIds = getBookingCardRoomIds(b);
      if (!roomIds.some((id) => Number(id) === roomIdNum)) continue;

      const bookingStart = b.check_in_date || b.start_date || b.start;
      const bookingEnd = b.check_out_date || b.end_date || b.end;
      if (!bookingStart || !bookingEnd) continue;
      const isSameDay = bookingStart === bookingEnd;
      const isActiveOnDate = isSameDay
        ? bookingStart <= selectedDateStr && bookingEnd >= selectedDateStr
        : bookingStart <= selectedDateStr && bookingEnd > selectedDateStr;
      // Arrival today (or still staying) means the room is not free for quick book.
      if (isActiveOnDate || bookingStart === selectedDateStr) return true;
    }
    return false;
  };

  bookings.forEach((b) => {
    const bookingStatus = String(b.status || '').toLowerCase();
    if (!inHouseStatuses.has(bookingStatus)) return;
    const checkOutDate = b.check_out_date || b.end_date || b.end;
    if (!checkOutDate || checkOutDate > selectedDateStr) return;
    // Keep unmarked checkouts on the board through hotel today, not future dates.
    if (hotelTodayStr && selectedDateStr > hotelTodayStr) return;

    const vacatedRoomIds = new Set(
      (Array.isArray(b.checked_out_room_ids) ? b.checked_out_room_ids : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id)),
    );
    (Array.isArray(b.room_segments) ? b.room_segments : []).forEach((seg) => {
      const segRoomId = Number(seg?.room_id);
      if (!Number.isFinite(segRoomId)) return;
      const segStatus = String(seg?.item_status || '').toLowerCase();
      if (seg?.checkout === true || segStatus === 'checked_out') {
        vacatedRoomIds.add(segRoomId);
      }
    });
    const roomIds = getBookingCardRoomIds(b);
    const segmentByRoomId = new Map(
      (Array.isArray(b.room_segments) ? b.room_segments : [])
        .map((seg) => [Number(seg?.room_id), seg])
        .filter(([id]) => Number.isFinite(id)),
    );
    roomIds.forEach((roomId) => {
      const roomIdNum = Number(roomId);
      if (!Number.isFinite(roomIdNum) || vacatedRoomIds.has(roomIdNum)) return;

      const existing = bookingsByRoomId[roomIdNum];
      const seg = segmentByRoomId.get(roomIdNum);
      const roomCheckedIn =
        (
          existing?.checkout !== true
          && (
            existing?.checked_in === true
            || String(existing?.item_status || '').toLowerCase() === 'checked_in'
          )
        )
        || (
          seg?.checkout !== true
          && (
            seg?.checked_in === true
            || String(seg?.item_status || '').toLowerCase() === 'checked_in'
          )
        );
      // Pending checkout overlay only for rooms actually in-house (not merely allocated).
      if (!roomCheckedIn) return;

      if (existing && isBlockingRoomBooking(existing)) {
        const existingStatus = String(existing.item_status || existing.status || '').toLowerCase();
        const existingCheckout = existing.check_out_date || existing.end_date || existing.end || '';
        const existingIsOtherInHouseStay =
          getBookingIdentity(existing) !== getBookingIdentity(b)
          && inHouseStatuses.has(existingStatus)
          && existingCheckout
          && existingCheckout > selectedDateStr;
        if (existingIsOtherInHouseStay) return;
      }

      const hadOtherBooking =
        (existing
          && isBlockingRoomBooking(existing)
          && getBookingIdentity(existing) !== getBookingIdentity(b))
        || roomHasOtherBookingOnSelectedDate(roomIdNum, b);

      bookingsByRoomId[roomIdNum] = {
        ...(existing || {}),
        ...b,
        id: b.id || b.booking_id,
        booking_id: b.booking_id || b.id,
        checked_in: true,
        item_status: 'checked_in',
        status_color: 'green',
        _pending_checkout: true,
        _has_next_booking: Boolean(hadOtherBooking),
        _allocation_key:
          existing?._allocation_key
          || getAllocationKey(b)
          || `pending_checkout_${b.id || b.booking_id}_${roomIdNum}`,
      };
    });
  });

  // After Checkout Now / Cancel, show the preferred terminal booking for the room on that day.
  // Nightly booking_items omit on/after actual checkout, so departed rooms otherwise look vacant.
  // Cancelled is lowest priority — checkout/OUT (or any higher status) wins on the card.
  const applyTerminalBookingOverlay = (b, terminalStatus) => {
    const checkInDate = b.check_in_date || b.start_date || b.start;
    const checkOutDate = b.check_out_date || b.end_date || b.end;
    if (!checkInDate || !checkOutDate) return;

    const isSameDay = checkInDate === checkOutDate;
    const isActiveOnSelectedDate = isSameDay
      ? checkInDate <= selectedDateStr && checkOutDate >= selectedDateStr
      : checkInDate <= selectedDateStr && checkOutDate > selectedDateStr;
    // Checkout-day overlay: checked_out bookings also appear on actual departure date.
    const isCheckoutDay =
      terminalStatus === 'checked_out' && checkOutDate === selectedDateStr;
    if (!isActiveOnSelectedDate && !isCheckoutDay) return;

    const roomIds = getBookingCardRoomIds(b);
    roomIds.forEach((roomId) => {
      const roomIdNum = Number(roomId);
      if (!Number.isFinite(roomIdNum)) return;

      const existing = bookingsByRoomId[roomIdNum];
      const candidate = {
        ...b,
        id: b.id || b.booking_id,
        booking_id: b.booking_id || b.id,
        status: terminalStatus,
        item_status: terminalStatus,
        checkout: terminalStatus === 'checked_out' ? true : Boolean(b.checkout),
        status_color: b.status_color || getFallbackStatusColor(terminalStatus),
        ...(terminalStatus === 'checked_out' ? { _checked_out_today: true } : { _cancelled_on_board: true }),
        _allocation_key:
          getAllocationKey(b) || `${terminalStatus}_${b.id || b.booking_id}_${roomIdNum}`,
      };
      if (!shouldReplaceRoomBooking(existing, candidate)) return;

      bookingsByRoomId[roomIdNum] = candidate;
    });
  };

  const applyPartialCheckoutVacatedOverlay = (b) => {
    const vacatedRoomIds = new Set(
      (Array.isArray(b.checked_out_room_ids) ? b.checked_out_room_ids : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id)),
    );
    const segmentByRoomId = new Map();
    (Array.isArray(b.room_segments) ? b.room_segments : []).forEach((segment) => {
      const roomId = Number(segment?.room_id);
      if (!Number.isFinite(roomId)) return;
      segmentByRoomId.set(roomId, segment);
      const segmentStatus = String(segment?.item_status || '').toLowerCase();
      if (segment?.checkout === true || segmentStatus === 'checked_out') {
        vacatedRoomIds.add(roomId);
      }
    });

    vacatedRoomIds.forEach((roomIdNum) => {
      const segment = segmentByRoomId.get(roomIdNum) || {};
      const effectiveCheckoutDate =
        getHotelDateKey(segment.segment_ended_at)
        || getHotelDateKey(b.check_out_time)
        || segment.end_date
        || b.check_out_date
        || b.end_date
        || b.end;
      if (effectiveCheckoutDate && effectiveCheckoutDate > selectedDateStr) return;
      const existing = bookingsByRoomId[roomIdNum];
      const candidate = {
        ...b,
        ...segment,
        id: b.id || b.booking_id,
        booking_id: b.booking_id || b.id,
        room_id: roomIdNum,
        room_ids: [roomIdNum],
        status: 'checked_out',
        item_status: 'checked_out',
        checkout: true,
        checked_in: false,
        check_in_date: segment.start_date || b.check_in_date || b.start_date || b.start,
        check_out_date: effectiveCheckoutDate || b.check_out_date || b.end_date || b.end,
        check_out_time: segment.segment_ended_at || b.check_out_time,
        status_color: getFallbackStatusColor('checked_out'),
        _checked_out_today: effectiveCheckoutDate === selectedDateStr,
        _allocation_key:
          getAllocationKey({ ...b, ...segment, room_id: roomIdNum })
          || `partial_checkout_${b.id || b.booking_id}_${roomIdNum}`,
      };
      if (!shouldReplaceRoomBooking(existing, candidate)) return;
      bookingsByRoomId[roomIdNum] = candidate;
    });
  };

  bookings.forEach((b) => {
    const bookingStatus = String(b.status || '').toLowerCase();
    if (bookingStatus === 'checked_out') {
      applyTerminalBookingOverlay(b, 'checked_out');
    } else if (bookingStatus === 'partial_checkout' || bookingStatus === 'partial checkout') {
      applyPartialCheckoutVacatedOverlay(b);
    } else if (bookingStatus === 'cancelled' || bookingStatus === 'canceled' || bookingStatus === 'no_show') {
      applyTerminalBookingOverlay(b, bookingStatus === 'canceled' ? 'cancelled' : bookingStatus);
    }
  });

  // Keep unallocated candidates ordered by status priority so allocation prefers active bookings.
  Object.keys(bookingsByRoomTypeId).forEach((roomTypeId) => {
    bookingsByRoomTypeId[roomTypeId].sort((a, b) => {
      const aPriority = getBookingStatusPriority(a?.status, a?.item_status);
      const bPriority = getBookingStatusPriority(b?.status, b?.item_status);
      return bPriority - aPriority;
    });
  });

  // Group rooms based on groupView: 'full', 'floor', or 'room_type'
  const roomsByType = {};
  const roomsByFloor = {};
  const roomsByAll = [];
  
  rooms.forEach(room => {
    if (groupView === 'floor') {
      // Group by floor when floor view is enabled
      const floorId = room.floor_id || room.floor_number || 'none';
      const floorKey = floorId !== 'none' ? floorId : 'none';
      if (!roomsByFloor[floorKey]) {
        roomsByFloor[floorKey] = {
          floor_id: room.floor_id,
          floor_number: room.floor_number,
          floor_name: room.floor_name || (room.floor_number ? `Floor ${room.floor_number}` : 'No Floor'),
          rooms: []
        };
      }
      roomsByFloor[floorKey].rooms.push(room);
    } else if (groupView === 'room_type') {
      // Group by room type
    const typeId = room.room_type_id || 'none';
    if (!roomsByType[typeId]) {
      roomsByType[typeId] = [];
    }
    roomsByType[typeId].push(room);
    } else {
      // Full view - all rooms together
      roomsByAll.push(room);
    }
  });

  // Compute totals
  const totals = {
    total: rooms.length,
    occupied: 0,
    hourly: 0,
    pending_checkout: 0,
    booked: 0,
    blocked: 0,
    vacant: 0,
    checked_out: 0,
    cancelled: 0,
  };
  let cardsHtml = [];

  // GLOBAL blocked room allocation (across all floors) - only for floor view
  // This ensures that if we block 1 room, only 1 room is allocated globally, not 1 per floor
  const globallyAllocatedBlockedRoomIds = new Set();
  
  // GLOBAL booking allocation (across all floors) - only for floor view
  // This ensures that unallocated bookings are assigned only once globally, not once per floor
  const globallyAllocatedBookingKeys = new Set(); // Track allocation keys that have been allocated
  const globallyAllocatedRoomBookings = new Map(); // Map room ID -> booking payload
  
  if (groupView === 'floor') {
    // Get all unique room types that have blocks
    const roomTypesWithBlocks = new Set();
    Object.keys(blockedRoomsByRoomTypeId).forEach(roomTypeId => {
      roomTypesWithBlocks.add(roomTypeId);
    });

    // For each room type with blocks, allocate rooms globally (across all floors)
    roomTypesWithBlocks.forEach(roomTypeId => {
      const blockInfo = blockedRoomsByRoomTypeId[roomTypeId];
      const numBlockedRooms = blockInfo.num_rooms || 0;
      const explicitRoomIds = Array.isArray(blockInfo.room_ids)
        ? blockInfo.room_ids.map((id) => Number(id)).filter(Boolean)
        : [];
      
      if (numBlockedRooms <= 0 && explicitRoomIds.length === 0) return;

      // Get all rooms of this type, sorted by room number (across all floors)
      const allRoomsOfType = rooms
        .filter(r => r.room_type_id === parseInt(roomTypeId))
        .sort((a, b) => {
          const numA = parseInt(a.room_number?.replace(/\D/g, '') || a.id) || a.id;
          const numB = parseInt(b.room_number?.replace(/\D/g, '') || b.id) || b.id;
          return numA - numB;
        });

      // Filter out rooms that have bookings
      const roomsWithBookingsForType = new Set();
      allRoomsOfType.forEach(room => {
        if (isBlockingRoomBooking(bookingsByRoomId[room.id])) {
          roomsWithBookingsForType.add(room.id);
        }
      });

      let roomsToBlock;
      if (explicitRoomIds.length > 0) {
        const explicitSet = new Set(explicitRoomIds);
        roomsToBlock = allRoomsOfType.filter((room) => explicitSet.has(Number(room.id)));
      } else {
        // Allocate first N available rooms globally
        const availableRooms = allRoomsOfType.filter(room => !roomsWithBookingsForType.has(room.id));
        roomsToBlock = availableRooms.slice(0, numBlockedRooms);
      }
      
      // Add to global allocation
      roomsToBlock.forEach(room => {
        globallyAllocatedBlockedRoomIds.add(room.id);
      });
    });
    
    // GLOBAL booking allocation: Allocate unallocated bookings to rooms globally (across all floors)
    // Sort all rooms by room number for consistent ascending order allocation
    const allRoomsSorted = [...rooms].sort((a, b) => {
      const numA = parseInt(a.room_number?.replace(/\D/g, '') || a.id) || a.id;
      const numB = parseInt(b.room_number?.replace(/\D/g, '') || b.id) || b.id;
      return numA - numB;
    });
    
    // Process each room type that has unallocated bookings
    Object.keys(bookingsByRoomTypeId).forEach(roomTypeId => {
      const unallocatedBookings = [...bookingsByRoomTypeId[roomTypeId]].sort((a, b) => {
        const aPriority = getBookingStatusPriority(a?.status, a?.item_status);
        const bPriority = getBookingStatusPriority(b?.status, b?.item_status);
        return bPriority - aPriority;
      });
      
      // Get all rooms of this type, sorted by room number
      const roomsOfType = allRoomsSorted.filter(r => r.room_type_id === parseInt(roomTypeId));
      
      // For each unallocated booking, find the first available room
      unallocatedBookings.forEach(booking => {
        const bookingId = booking.id || booking.booking_id;
        const allocationKey = getAllocationKey(booking) || `booking_${bookingId}_${roomTypeId}`;
        
        // Skip if this booking is already globally allocated
        if (globallyAllocatedBookingKeys.has(allocationKey)) {
          return;
        }
        
        // Check if booking is active on selected date
        const bookingStart = booking.check_in_date || booking.start_date || booking.start;
        const bookingEnd = booking.check_out_date || booking.end_date || booking.end;
        if (!bookingStart || !bookingEnd) return;
        
        const isSameDay = bookingStart === bookingEnd;
        const isActiveOnSelectedDate = isSameDay
          ? bookingStart <= selectedDateStr && bookingEnd >= selectedDateStr
          : bookingStart <= selectedDateStr && bookingEnd > selectedDateStr;
        
        if (!isActiveOnSelectedDate) return;
        
        // Find first available room (not blocked, not already allocated to another booking)
        const availableRoom = roomsOfType.find(room => {
          // Room must not be blocked globally
          if (globallyAllocatedBlockedRoomIds.has(room.id)) return false;
          
          if (!canUseRoomForBooking(bookingsByRoomId[room.id], booking)) return false;
          
          // Room must not be allocated to another booking globally
          if (globallyAllocatedRoomBookings.has(room.id)) return false;
          
          return true;
        });
        
        if (availableRoom) {
          // Allocate this booking to this room globally
          const normalizedBooking = {
            ...booking,
            id: bookingId,
            booking_id: booking.booking_id || bookingId,
            _allocation_key: allocationKey,
          };
          globallyAllocatedBookingKeys.add(allocationKey);
          globallyAllocatedRoomBookings.set(availableRoom.id, normalizedBooking);
          
          // Also update bookingsByRoomId for this room
          bookingsByRoomId[availableRoom.id] = normalizedBooking;
        }
      });
    });
  }

  /**
   * Distinct rooms that departed on the selected date (checked out today).
   * Includes rooms that already turned over to a new stay — those no longer
   * paint as OUT on the card but still count toward Out / occupancy.
   */
  const countDeparturesOnSelectedDate = (scopeRoomIds = null) => {
    const scope = scopeRoomIds
      ? new Set([...scopeRoomIds].map((id) => Number(id)).filter((id) => Number.isFinite(id)))
      : null;
    const includeRoom = (roomId) => {
      const n = Number(roomId);
      if (!Number.isFinite(n)) return false;
      if (scope && !scope.has(n)) return false;
      return true;
    };
    const departed = new Set();

    (bookings || []).forEach((b) => {
      const status = String(b.status || '').toLowerCase();
      const checkOutDate = getHotelDateKey(b.check_out_time) || b.check_out_date || b.end_date || b.end;
      const bookingCheckoutMatches = Boolean(checkOutDate && String(checkOutDate) === String(selectedDateStr));

      const vacated = new Set(
        (Array.isArray(b.checked_out_room_ids) ? b.checked_out_room_ids : [])
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id)),
      );

      if (status === 'checked_out' && bookingCheckoutMatches) {
        const ids = getBookingCardRoomIds(b);
        ids.forEach((id) => {
          if (includeRoom(id)) departed.add(Number(id));
        });
      } else if (vacated.size > 0 && bookingCheckoutMatches) {
        // Partial checkout: only rooms already vacated today.
        vacated.forEach((id) => {
          if (includeRoom(id)) departed.add(id);
        });
      }

      (Array.isArray(b.room_segments) ? b.room_segments : []).forEach((segment) => {
        const segmentStatus = String(segment?.item_status || '').toLowerCase();
        if (segment?.checkout !== true && segmentStatus !== 'checked_out') return;
        const segmentCheckoutDate = getHotelDateKey(segment.segment_ended_at) || checkOutDate;
        if (String(segmentCheckoutDate) !== String(selectedDateStr)) return;
        const roomId = Number(segment?.room_id);
        if (includeRoom(roomId)) departed.add(roomId);
      });
    });

    Object.keys(selectedDateItems || {}).forEach((roomTypeId) => {
      const byRoom = selectedDateItems[roomTypeId] || {};
      Object.keys(byRoom).forEach((roomId) => {
        if (!includeRoom(roomId)) return;
        const items = byRoom[roomId] || [];
        const hasCheckoutItem = items.some((item) => {
          const st = String(item?.item_status || item?.status || '').toLowerCase();
          return st === 'checked_out' || item?.checkout === true;
        });
        if (hasCheckoutItem) departed.add(Number(roomId));
      });
    });

    Object.keys(bookingsByRoomId || {}).forEach((roomId) => {
      if (!includeRoom(roomId)) return;
      const booking = bookingsByRoomId[roomId];
      if (!booking) return;
      const st = String(booking.item_status || booking.status || '').toLowerCase();
      if (
        booking._checked_out_today
        || st === 'checked_out'
        || (booking.checkout === true && st !== 'cancelled' && st !== 'canceled' && st !== 'no_show')
      ) {
        departed.add(Number(roomId));
      }
    });

    return departed.size;
  };

  // Function to process rooms for a group (either room type or floor)
  const processRoomGroup = (groupRooms, groupInfo, isFloorView = false) => {
    const groupCounts = {
      total: groupRooms.length,
      occupied: 0,
      hourly: 0,
      pending_checkout: 0,
      pending_with_next: 0,
      pending_free: 0,
      booked: 0,
      blocked: 0,
      vacant: 0,
      checked_out: 0,
      cancelled: 0,
      departures_today: countDeparturesOnSelectedDate(groupRooms.map((r) => r.id)),
    };

    // Sort rooms by room number in ascending order for proper blocked room allocation
    const sortedGroupRooms = [...groupRooms].sort((a, b) => {
      // Extract numeric part from room number for proper sorting
      const numA = parseInt(a.room_number?.replace(/\D/g, '') || a.id) || a.id;
      const numB = parseInt(b.room_number?.replace(/\D/g, '') || b.id) || b.id;
      return numA - numB;
    });

    // For floor view, use global allocation (rooms already allocated globally)
    // For room type view, allocate per room type group
    const blockedRoomIds = new Set();
    
    if (isFloorView) {
      // For floor view: use global allocation - only mark rooms that were allocated globally
      sortedGroupRooms.forEach(room => {
        if (globallyAllocatedBlockedRoomIds.has(room.id)) {
          blockedRoomIds.add(room.id);
        }
      });
    } else {
      // For room type / full view: prefer explicit room_ids; else first-N available fallback
      const blockedRoomsByTypeInGroup = {};
      sortedGroupRooms.forEach(room => {
        const roomTypeId = room.room_type_id;
        if (roomTypeId && blockedRoomsByRoomTypeId[roomTypeId]) {
          if (!blockedRoomsByTypeInGroup[roomTypeId]) {
            const info = blockedRoomsByRoomTypeId[roomTypeId];
            const explicitRoomIds = Array.isArray(info.room_ids)
              ? info.room_ids.map((id) => Number(id)).filter(Boolean)
              : [];
            blockedRoomsByTypeInGroup[roomTypeId] = {
              count: 0,
              max: info.num_rooms || 0,
              info,
              explicitRoomIds,
              explicitSet: new Set(explicitRoomIds),
            };
          }
        }
      });

      // First pass: identify which rooms have bookings
      const roomsWithBookings = new Set();
      sortedGroupRooms.forEach(room => {
        if (isBlockingRoomBooking(bookingsByRoomId[room.id])) {
          roomsWithBookings.add(room.id);
        }
      });

      // Second pass: allocate blocked rooms
      sortedGroupRooms.forEach(room => {
        const roomTypeId = room.room_type_id;
        const blockInfoForType = blockedRoomsByTypeInGroup[roomTypeId];
        if (!blockInfoForType) return;

        const roomIdNum = Number(room.id);
        if (blockInfoForType.explicitSet.size > 0) {
          // Explicit room selection (e.g. block on 103) — never remap to first available
          if (blockInfoForType.explicitSet.has(roomIdNum)) {
            blockedRoomIds.add(room.id);
          }
          return;
        }

        // Legacy quantity-only blocks: first N available rooms of this type
        if (
          blockInfoForType.max > 0 &&
          blockInfoForType.count < blockInfoForType.max &&
          !roomsWithBookings.has(room.id)
        ) {
          blockedRoomIds.add(room.id);
          blockInfoForType.count++;
        }
      });
    }

    // Get block info for display (used in room status)
    const getBlockInfoForRoom = (room) => {
      const roomTypeId = room.room_type_id;
      const info = roomTypeId ? blockedRoomsByRoomTypeId[roomTypeId] : null;
      if (!info) return null;
      const explicitRoomIds = Array.isArray(info.room_ids)
        ? info.room_ids.map((id) => Number(id)).filter(Boolean)
        : [];
      if (explicitRoomIds.length > 0 && !explicitRoomIds.includes(Number(room.id))) {
        return null;
      }
      return info;
    };

    const roomBoxes = sortedGroupRooms.map((room, roomIndex) => {
      let status = 'vacant';
      let guestLabel = '';
      let metaLabel = '';
      let statusMessage = '';
      let bookingCode = '';
      let roomBlockInfo = null;

      // Check for booking by room_id first
      let booking = bookingsByRoomId[room.id];
      
      // If no booking found by room_id, check if this room has been globally allocated a booking
      // (This only applies to floor view where bookings are allocated globally)
      if (!booking && isFloorView) {
        const allocatedBooking = globallyAllocatedRoomBookings.get(room.id);
        if (allocatedBooking) {
          booking = allocatedBooking;
        }
      }
      
      // For non-floor views, use the original logic (per-group allocation)
      if ((!booking || !isBlockingRoomBooking(booking)) && !isFloorView) {
        const roomTypeId = room.room_type_id;
        if (bookingsByRoomTypeId[roomTypeId] && bookingsByRoomTypeId[roomTypeId].length > 0) {
          // Find first booking for this room type that:
          // 1. Hasn't been assigned yet to a room in this group
          // 2. Is active on the selected date (check date range)
          const availableBooking = bookingsByRoomTypeId[roomTypeId].find(b => {
            // Check if this booking is already assigned to a room in this group
            const candidateKey = getAllocationKey(b);
            const alreadyAssigned = sortedGroupRooms.some(
              (r) => getAllocationKey(bookingsByRoomId[r.id]) === candidateKey
            );
            if (alreadyAssigned) return false;
            if (isRoomCheckedOutForBooking(b, room.id)) return false;
            
            // Check if booking is active on selected date using check_in_date and check_out_date
            // For checked-out bookings, check_out_date contains actual checkout date (from API)
            const bookingStart = b.check_in_date || b.start_date || b.start;
            const bookingEnd = b.check_out_date || b.end_date || b.end;
            if (!bookingStart || !bookingEnd) return false;
            
            // For same-day bookings (checkInDate === checkOutDate): show on that date (inclusive)
            // For multi-day bookings (checkInDate < checkOutDate): show only up to but not including checkout date (exclusive)
            // Booking is active if: checkInDate <= selectedDate < checkOutDate (or <= for same-day)
            const isSameDay = bookingStart === bookingEnd;
            const isActiveOnSelectedDate = isSameDay
              ? bookingStart <= selectedDateStr && bookingEnd >= selectedDateStr
              : bookingStart <= selectedDateStr && bookingEnd > selectedDateStr;
            return isActiveOnSelectedDate;
        });
        
        if (availableBooking) {
            // Assign to first vacant room of this type (that's not blocked)
            // Sort by room number to ensure ascending order allocation (same as grid view)
            const isFirstVacantRoom = !blockedRoomIds.has(room.id) && 
              canUseRoomForBooking(bookingsByRoomId[room.id], availableBooking);
          if (isFirstVacantRoom) {
              booking = {
                ...availableBooking,
                // Ensure id field exists
                id: availableBooking.id || availableBooking.booking_id,
                booking_id: availableBooking.booking_id || availableBooking.id,
                _allocation_key: getAllocationKey(availableBooking),
              };
            // Mark this booking as assigned to this room for this render
            bookingsByRoomId[room.id] = booking;
            }
          }
        }
      }
      
      // Check if this room is blocked (explicit room_ids or quantity fallback)
      if (blockedRoomIds.has(room.id)) {
        roomBlockInfo = getBlockInfoForRoom(room);
        status = 'blocked';
        statusMessage = 'BLOCKED';
        guestLabel = '';
        metaLabel = roomBlockInfo?.reason || 'Blocked';
      } else if (booking) {
        // Get guest name from multiple possible fields
        guestLabel = booking.guest_name || booking.guestName || booking.title || 'Guest';
        // Get booking code - handle empty string as well
        bookingCode = booking.code || booking.booking_code || '';
        metaLabel = bookingCode ? `#${bookingCode}` : '';
        
        // Prefer item-level status so partial check-in/out remains room-wise, not booking-wide.
        const effectiveStatus = resolveRoomCardItemStatus(booking);
        const statusColor =
          booking.status_color || getFallbackStatusColor(effectiveStatus || booking.status);
        const isCheckedOut =
          effectiveStatus === 'checked_out'
          || booking.checkout === true
          || isRoomCheckedOutForBooking(booking, room.id)
          || booking.status === 'checked_out';

        if (isCheckedOut) {
          status = 'checked_out';
        } else if (effectiveStatus === 'checked_in') {
          const checkoutDate = booking.check_out_date || booking.end_date || booking.end || '';
          // Same-day / day-use stays are Hourly, not Pending Checkout.
          const sameDayStay = isSameDayStay(booking);
          const isDueCheckout =
            (!hotelTodayStr || selectedDateStr <= hotelTodayStr)
            && (
              booking._pending_checkout === true
              || (Boolean(checkoutDate) && checkoutDate <= selectedDateStr)
            );
          if (sameDayStay) {
            status = 'hourly';
          } else if (isDueCheckout) {
            status = 'pending_checkout';
          } else {
            status = 'occupied';
          }
        } else if (effectiveStatus === 'cancelled' || effectiveStatus === 'no_show' || statusColor === 'red') {
          status = 'cancelled';
        } else if (statusColor === 'orange' || effectiveStatus === 'confirmed') {
          status = 'booked';
        } else {
          status = 'booked';
        }
      }

      groupCounts[status] += 1;
      if (status === 'pending_checkout') {
        if (booking?._has_next_booking) {
          groupCounts.pending_with_next += 1;
        } else {
          groupCounts.pending_free += 1;
        }
      }
      totals[status] += 1;

      // Check if this room's item is checked out (for partial_checkout, use item-level checkout)
      const isCheckedOut = status === 'checked_out';
      const isDisabled = isCheckedOut;

      // For checked_out status, always show as disabled with blue color
      const showAsDisabled = status === 'checked_out';

      // Status palette drives card background, border, and accent colors.
      const colors = getUltraRoomStatusPalette(status, isDark);

      const maintenanceBlocked = isRoomMaintenanceBlockedOn(room, selectedDateStr);
      const maintenanceBlockLabel = maintenanceBlocked
        ? getRoomMaintenanceBlockLabel(room, selectedDateStr)
        : '';
      const activeWebCheckInRaw = room?.active_web_checkin || null;
      const inviteExpired =
        Boolean(activeWebCheckInRaw?.expires_at)
        && moment(activeWebCheckInRaw.expires_at).valueOf() <= Date.now();
      const activeWebCheckIn = inviteExpired ? null : activeWebCheckInRaw;
      const hasActiveWebCheckIn = Boolean(
        !inviteExpired && (activeWebCheckInRaw?.id || room?.has_active_web_checkin)
      );
      // Pending checkout: allow Quick Book only when the room has no next reservation.
      const pendingCheckoutQuickOk =
        status === 'pending_checkout' && !booking?._has_next_booking;
      const quickEligible =
        Boolean(onQuickBook)
        && isQuickBookDateAllowed(selectedDateStr, hotelTimezone)
        && status !== 'blocked'
        && !maintenanceBlocked
        && !hasActiveWebCheckIn
        && !isRoomDirty(room)
        && (
          status === 'vacant'
          || status === 'checked_out'
          || status === 'cancelled'
          || pendingCheckoutQuickOk
        );
      const webCheckInEligible =
        canManageWebCheckIn
        && Boolean(onWebCheckIn)
        && isQuickBookDateAllowed(selectedDateStr, hotelTimezone)
        && !maintenanceBlocked
        && !hasActiveWebCheckIn
        && (
          status === 'vacant'
          || status === 'checked_out'
          || status === 'cancelled'
        );
      const bookingId = booking?.id || booking?.booking_id;
      const checkoutEligible =
        (status === 'occupied' || status === 'hourly' || status === 'pending_checkout')
        && Boolean(bookingId)
        && Boolean(onCheckout);
      const checkInEligible =
        status === 'booked' && Boolean(bookingId) && Boolean(onCheckIn) && !maintenanceBlocked;
      const blockEligible = Boolean(
        onEventBlock
        && room?.room_type_id
        && !['occupied', 'hourly', 'pending_checkout', 'booked'].includes(status)
        && !hasActiveWebCheckIn,
      );
      const pendingCleaningId = pendingCleaningByRoomId?.[String(room.id)];
      const dirtyRoomEligible = isRoomDirty(room) && Boolean(onDirtyRoomHousekeeping);
      const cleaningEligible = Boolean(onHousekeeping && (pendingCleaningId || dirtyRoomEligible));
      const openQuickBook = () => {
        if (!quickEligible || !onQuickBook) return;
        const rtName =
          room.room_type_name ||
          room_types.find((t) => Number(t.id) === Number(room.room_type_id))?.name ||
          '';
        onQuickBook({
          roomId: room.id,
          roomNumber: room.room_number,
          roomTypeId: room.room_type_id,
          roomTypeName: rtName,
          checkInDate: selectedDateStr,
        });
      };
      const openActiveWebCheckIn = () => {
        if (
          !canManageWebCheckIn
          || !hasActiveWebCheckIn
          || !activeWebCheckIn?.id
          || !onOpenWebCheckInInvite
        ) return;
        onOpenWebCheckInInvite(activeWebCheckIn);
      };
      const handleRoomCardClick = () => {
        if (hasActiveWebCheckIn) {
          openActiveWebCheckIn();
          return;
        }
        if (dirtyRoomEligible) {
          onDirtyRoomHousekeeping(room);
          return;
        }
        // Hourly / pending checkout: default action is checkout; quick book stays in the menu.
        if ((status === 'hourly' || status === 'pending_checkout') && checkoutEligible) {
          onCheckout(bookingId);
          return;
        }
        if (quickEligible) {
          openQuickBook();
          return;
        }
        if (status === 'blocked' && onEventBlock && room?.room_type_id) {
          const rtName =
            room.room_type_name ||
            room_types.find((t) => Number(t.id) === Number(room.room_type_id))?.name ||
            '';
          onEventBlock({
            roomId: room.id,
            roomTypeId: room.room_type_id,
            roomTypeName: rtName,
            startDate: selectedDateStr,
            manageTab: true,
          });
          return;
        }
        if (booking && bookingId) {
          setSelectedBookingId(bookingId);
          setDrawerOpen(true);
        }
      };
      const statusLabel =
        hasActiveWebCheckIn
          ? 'Web Check-in'
          : status === 'occupied'
          ? 'In-house'
          : status === 'hourly'
            ? 'Hourly'
            : status === 'pending_checkout'
              ? 'Pending Checkout'
              : status === 'booked'
                ? 'Booked'
                : status === 'checked_out'
                  ? 'Out'
                  : status === 'cancelled'
                    ? 'Cancelled'
                    : status === 'blocked'
                      ? 'Blocked'
                      : 'Open';
      const hasNamedGuest =
        !hasActiveWebCheckIn
        && ['occupied', 'hourly', 'pending_checkout', 'booked', 'checked_out', 'cancelled'].includes(status)
        && booking
        && guestLabel
        && guestLabel !== 'Guest';
      const nameLine = hasActiveWebCheckIn
        ? (activeWebCheckIn?.guest_name || 'Web check-in pending')
        : hasNamedGuest
        ? guestLabel
        : status === 'vacant'
          ? 'Vacant'
          : status === 'blocked'
            ? guestLabel || 'Blocked'
            : 'Guest';
      const nameLineMuted = hasActiveWebCheckIn ? false : !hasNamedGuest;
      return (
        <UltraRoomGridCard
          key={room.id}
          room={room}
          status={status}
          statusLabel={statusLabel}
          colors={colors}
          isDark={isDark}
          isMobileLayout={isMobileLayout}
          isWorkspaceLayout={isWorkspaceLayout}
          isLoading={isLoading || room.__placeholder}
          nameLine={nameLine}
          nameLineMuted={nameLineMuted}
          booking={booking}
          bookingCode={bookingCode}
          showAsDisabled={showAsDisabled}
          quickEligible={quickEligible}
          webCheckInEligible={webCheckInEligible}
          checkoutEligible={checkoutEligible}
          checkInEligible={checkInEligible}
          blockEligible={blockEligible}
          cleaningEligible={cleaningEligible}
          dirtyRoomEligible={dirtyRoomEligible}
          maintenanceBlocked={maintenanceBlocked}
          maintenanceBlockLabel={maintenanceBlockLabel}
          blockInfo={status === 'blocked' ? roomBlockInfo : null}
          activeWebCheckIn={activeWebCheckIn}
          onCardClick={handleRoomCardClick}
          onQuickBook={quickEligible ? openQuickBook : undefined}
          onOpenWebCheckInInvite={
            canManageWebCheckIn && hasActiveWebCheckIn ? openActiveWebCheckIn : undefined
          }
          onWebCheckIn={
            webCheckInEligible
              ? () => {
                  const rtName =
                    room.room_type_name ||
                    room_types.find((t) => Number(t.id) === Number(room.room_type_id))?.name ||
                    '';
                  onWebCheckIn?.({
                    roomId: room.id,
                    roomNumber: room.room_number,
                    roomTypeId: room.room_type_id,
                    roomTypeName: rtName,
                    checkInDate: selectedDateStr,
                  });
                }
              : undefined
          }
          onViewBooking={() => {
            if (bookingId) {
              setSelectedBookingId(bookingId);
              setDrawerOpen(true);
            }
          }}
          onCheckIn={() => {
            if (bookingId) {
              onCheckIn(bookingId);
            }
          }}
          onCheckout={() => {
            if (bookingId) {
              onCheckout(bookingId);
            }
          }}
          onEventBlock={() => {
            if (room?.room_type_id) {
              const rtName =
                room.room_type_name ||
                room_types.find((t) => Number(t.id) === Number(room.room_type_id))?.name ||
                '';
              onEventBlock({
                roomId: room.id,
                roomTypeId: room.room_type_id,
                roomTypeName: rtName,
                startDate: selectedDateStr,
                manageTab: status === 'blocked',
              });
            }
          }}
          onMarkCleaning={() => {
            if (dirtyRoomEligible) {
              onDirtyRoomHousekeeping(room);
              return;
            }
            if (pendingCleaningId) {
              onHousekeeping(pendingCleaningId);
            }
          }}
        />
      );
    });

    // Vacant rooms are those without any active display status.
    groupCounts.vacant = Math.max(
      0,
      groupCounts.total -
        groupCounts.occupied -
        groupCounts.hourly -
        groupCounts.pending_checkout -
        groupCounts.booked -
        groupCounts.blocked -
        groupCounts.checked_out -
        groupCounts.cancelled
    );

    return { roomBoxes, groupCounts };
  };

  // Process rooms based on groupView
  if (groupView === 'full') {
    // Full view - all rooms together
    const { roomBoxes, groupCounts } = processRoomGroup(roomsByAll, { id: 'all', name: 'All Rooms' }, false);

    cardsHtml.push(
      <Paper
        key="all-rooms"
        elevation={0}
        sx={sectionPaperSx}
      >
        <Box
          data-dashboard-all-rooms-anchor=""
          sx={{ ...sectionHeaderSx, position: 'relative', zIndex: 2 }}
        >
          {renderSectionTitle('All Rooms')}
          {renderGroupStats(groupCounts)}
        </Box>
        <UltraRoomGridContainer
          isDark={isDark}
          isMobileLayout={isMobileLayout}
          isWorkspaceLayout={isWorkspaceLayout}
        >
          {roomBoxes}
        </UltraRoomGridContainer>
      </Paper>
    );
  } else if (groupView === 'floor') {
    // Group by floor
    const floorKeys = Object.keys(roomsByFloor).sort((a, b) => {
      const floorA = roomsByFloor[a];
      const floorB = roomsByFloor[b];
      const numA = floorA.floor_number !== null && floorA.floor_number !== undefined ? floorA.floor_number : 999;
      const numB = floorB.floor_number !== null && floorB.floor_number !== undefined ? floorB.floor_number : 999;
      return numA - numB;
    });

    floorKeys.forEach(floorKey => {
      const floorData = roomsByFloor[floorKey];
      const { roomBoxes, groupCounts } = processRoomGroup(floorData.rooms, floorData, true);

      cardsHtml.push(
        <Paper
          key={`floor-${floorKey}`}
          elevation={0}
          sx={sectionPaperSx}
        >
          <Box sx={{ ...sectionHeaderSx, position: 'relative', zIndex: 2 }}>
            {renderSectionTitle(floorData.floor_name)}
            {renderGroupStats(groupCounts)}
          </Box>
          <UltraRoomGridContainer
            isDark={isDark}
            isMobileLayout={isMobileLayout}
            isWorkspaceLayout={isWorkspaceLayout}
          >
            {roomBoxes}
          </UltraRoomGridContainer>
        </Paper>
      );
    });
  } else if (groupView === 'room_type') {
    // Group by room type
    room_types.forEach(type => {
      const typeRooms = roomsByType[type.id] || [];
      const { roomBoxes, groupCounts } = processRoomGroup(typeRooms, type, false);

      cardsHtml.push(
        <Paper
          key={type.id}
          elevation={0}
          sx={sectionPaperSx}
        >
          <Box sx={{ ...sectionHeaderSx, position: 'relative', zIndex: 2 }}>
            {renderSectionTitle(type.name)}
            {renderGroupStats(groupCounts)}
          </Box>
          <UltraRoomGridContainer
            isDark={isDark}
            isMobileLayout={isMobileLayout}
            isWorkspaceLayout={isWorkspaceLayout}
          >
            {roomBoxes}
          </UltraRoomGridContainer>
        </Paper>
      );
    });
  }

  // Calculate vacant count
  totals.vacant = Math.max(
    0,
    totals.total
      - totals.occupied
      - totals.hourly
      - totals.pending_checkout
      - totals.booked
      - totals.blocked
      - totals.checked_out
      - totals.cancelled,
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Room Type Cards */}
      <Box>{cardsHtml}</Box>
      
      {/* Booking Details Drawer */}
      <BookingDetailsDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedBookingId(null);
        }}
        bookingId={selectedBookingId}
        theme={theme}
      />
    </Box>
  );
};

export default memo(BookingCalendarUltraView);